import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { authApi } from './services/api';

// Layouts
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';

// Auth Components
import PermissionRoute from './components/auth/PermissionRoute';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';

// Common Pages
import ProfilePage from './pages/ProfilePage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import HelpPage from './pages/HelpPage';

// Command Center Pages (Operations)
import DashboardPage from './pages/command-center/DashboardPage';
import AnalysisPage from './pages/command-center/AnalysisPage';

// Missions Pages
import MissionsPage from './pages/missions/MissionsPage';
import { MissionsListPage } from './pages/missions/MissionsListPage';
import { MissionDetailPage } from './pages/missions/MissionDetailPage';
import CreateMissionPage from './pages/missions/CreateMissionPage';
import MissionIngestionPage from './pages/missions/MissionIngestionPage';
import LargeFileUploadPage from './pages/missions/LargeFileUploadPage';

// Satellite Monitoring Pages
import { BoundariesMapPage } from './pages/satellite/BoundariesMapPage';
import { ImageryPage } from './pages/satellite/ImageryPage';
import { AnomalyDashboardPage } from './pages/satellite/AnomalyDashboardPage';

// AgroTech Pages
import AgroTechDashboardPage from './pages/agrotech/AgroTechDashboardPage';
import FieldsPage from './pages/agrotech/FieldsPage';
import TemporalAnalysisPage from './pages/agrotech/TemporalAnalysisPage';
import AnomalyAlertsPage from './pages/agrotech/AnomalyAlertsPage';
import SurveyMissionPlannerPage from './pages/agrotech/SurveyMissionPlannerPage';
import FlightAnalysisPage from './pages/agrotech/FlightAnalysisPage';
import AnalysisReviewPage from './pages/agrotech/AnalysisReviewPage';
import RemediationMissionsPage from './pages/agrotech/RemediationMissionsPage';
import TreatmentEfficacyPage from './pages/agrotech/TreatmentEfficacyPage';
import CropHealthReportsPage from './pages/agrotech/CropHealthReportsPage';
import ConversationalAnalysisPage from './pages/agrotech/ConversationalAnalysisPage';

// Precision Planning Pages
import VariableRateScriptingPage from './pages/precision/VariableRateScriptingPage';
import YieldForecastingPage from './pages/precision/YieldForecastingPage';
import CropSuitabilityPage from './pages/precision/CropSuitabilityPage';
import LandExpansionPage from './pages/precision/LandExpansionPage';
import ChemicalCompliancePage from './pages/precision/ChemicalCompliancePage';

// Infrastructure Intelligence Pages
import InfraDashboardPage from './pages/infrastructure/InfraDashboardPage';
import AssetBoundariesPage from './pages/infrastructure/AssetBoundariesPage';
import InfraTemporalAnalysisPage from './pages/infrastructure/InfraTemporalAnalysisPage';
import InfraAnomalyAlertsPage from './pages/infrastructure/InfraAnomalyAlertsPage';
import InfraSurveyMissionPlannerPage from './pages/infrastructure/InfraSurveyMissionPlannerPage';
import InfraFlightAnalysisPage from './pages/infrastructure/InfraFlightAnalysisPage';
import InfraAnalysisReviewPage from './pages/infrastructure/InfraAnalysisReviewPage';
import InfraResponsePlanningPage from './pages/infrastructure/InfraResponsePlanningPage';
import ConstructionProgressPage from './pages/infrastructure/ConstructionProgressPage';
import EncroachmentDetectionPage from './pages/infrastructure/EncroachmentDetectionPage';

// Oil & Gas Pages
import OilGasDashboardPage from './pages/oil-gas/OilGasDashboardPage';
import CorridorBoundariesPage from './pages/oil-gas/CorridorBoundariesPage';
import OilGasTemporalAnalysisPage from './pages/oil-gas/TemporalAnalysisPage';
import PipelineAnomalyAlertsPage from './pages/oil-gas/AnomalyAlertsPage';
import OgiSurveyPlannerPage from './pages/oil-gas/SurveyMissionPlannerPage';
import OilGasFlightAnalysisPage from './pages/oil-gas/FlightAnalysisPage';
import OilGasAnalysisReviewPage from './pages/oil-gas/AnalysisReviewPage';
import ResponsePlanningPage from './pages/oil-gas/ResponsePlanningPage';
import IncidentClosurePage from './pages/oil-gas/IncidentClosurePage';
import UpstreamExplorationPage from './pages/oil-gas/UpstreamExplorationPage';
import PlumeLeakAnalyzerPage from './pages/oil-gas/PlumeLeakAnalyzerPage';
import FacilitySafetyPage from './pages/oil-gas/FacilitySafetyPage';
import ErpIntegrationPage from './pages/oil-gas/ErpIntegrationPage';
import ValueRealizationPage from './pages/oil-gas/ValueRealizationPage';
import RegulatoryReportingPage from './pages/oil-gas/RegulatoryReportingPage';

// Environmental Geotech Pages
import EnvDashboardPage from './pages/environmental/EnvDashboardPage';
import ZoneBoundariesPage from './pages/environmental/ZoneBoundariesPage';
import EnvTemporalAnalysisPage from './pages/environmental/EnvTemporalAnalysisPage';
import EnvAnomalyAlertsPage from './pages/environmental/EnvAnomalyAlertsPage';
import EnvSurveyMissionPlannerPage from './pages/environmental/EnvSurveyMissionPlannerPage';
import EnvFlightAnalysisPage from './pages/environmental/EnvFlightAnalysisPage';
import EnvAnalysisReviewPage from './pages/environmental/EnvAnalysisReviewPage';
import EnforcementActionsPage from './pages/environmental/EnforcementActionsPage';
import CarbonCreditVerificationPage from './pages/environmental/CarbonCreditVerificationPage';
import GeohazardRiskPage from './pages/environmental/GeohazardRiskPage';

// Linear Assets Pages (P3)
import LinearAssetsDashboardPage from './pages/linear-assets/LinearAssetsDashboardPage';
import LinearCorridorBoundariesPage from './pages/linear-assets/LinearCorridorBoundariesPage';
import LinearTemporalAnalysisPage from './pages/linear-assets/LinearTemporalAnalysisPage';
import LinearEncroachmentPage from './pages/linear-assets/LinearEncroachmentPage';
import LinearTrackObstructionsPage from './pages/linear-assets/LinearTrackObstructionsPage';
import LinearSurveyPlannerPage from './pages/linear-assets/LinearSurveyPlannerPage';
import LinearFlightAnalysisPage from './pages/linear-assets/LinearFlightAnalysisPage';
import LinearAnalysisReviewPage from './pages/linear-assets/LinearAnalysisReviewPage';
import LinearResponsePlanningPage from './pages/linear-assets/LinearResponsePlanningPage';
import LinearWorkOrdersPage from './pages/linear-assets/LinearWorkOrdersPage';
import LinearReportsPage from './pages/linear-assets/LinearReportsPage';
import LinearEfficacyPage from './pages/linear-assets/LinearEfficacyPage';

// Platform Admin Pages
import DemoSetupPage from './pages/platform-admin/DemoSetupPage';
import PlatformAdminDashboardPage from './pages/platform-admin/PlatformAdminDashboardPage';
import { RoleRegistryPage } from './pages/platform-admin/RoleRegistryPage';
import OrganizationProvisioningPage from './pages/platform-admin/OrganizationProvisioningPage';
import PlatformAnalyticsPage from './pages/platform-admin/PlatformAnalyticsPage';
import MembershipRequestsPage from './pages/platform-admin/MembershipRequestsPage';
import PlatformUserManagementPage from './pages/platform-admin/PlatformUserManagementPage';
import UserManagementAuditPage from './pages/platform-admin/UserManagementAuditPage';
import ModuleMarketplace from './pages/platform-admin/ModuleMarketplace';

// Organization Settings Pages
import UserManagementPage from './pages/organization-settings/UserManagementPage';
import RoleScreenAccessPage from './pages/organization-settings/RoleScreenAccessPage';
import AIConfigurationPage from './pages/organization-settings/AIConfigurationPage';
import ApprovedAssetsPage from './pages/organization-settings/ApprovedAssetsPage';
import UnitsOfMeasureSettingsPage from './pages/organization-settings/UnitsOfMeasureSettingsPage';
import FlightDataPipelineSettingsPage from './pages/organization-settings/FlightDataPipelineSettingsPage';
import SatelliteMonitoringSettingsPage from './pages/organization-settings/SatelliteMonitoringSettingsPage';
import HierarchyManager from './pages/organization-settings/HierarchyManager';
import AuditTrailViewer from './pages/organization-settings/AuditTrailViewer';
import IntegrityDashboard from './pages/organization-settings/IntegrityDashboard';
import WebhooksConfiguration from './pages/organization-settings/WebhooksConfiguration';
import NotificationsCenter from './pages/organization-settings/NotificationsCenter';
import MFAManagement from './pages/organization-settings/MFAManagement';

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

// Public Route Component (redirect if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
}

function App() {
  const { isAuthenticated, token, clearAuth } = useAuthStore();

  // Fetch fresh user data on mount if authenticated
  useEffect(() => {
    const fetchUserData = async () => {
      if (isAuthenticated && token) {
        try {
          const response = await authApi.getCurrentUser();
          if (response.success && response.data.user) {
            useAuthStore.getState().updateUser({
              id: response.data.user.id,
              email: response.data.user.email,
              name: response.data.user.full_name,
              role: response.data.user.role,
              organization: response.data.user.organization_name || response.data.user.organization_id,
            });
          }
        } catch (error: any) {
          console.error('Failed to fetch user data:', error);
          // If 401 Unauthorized, clear auth state (invalid/expired token)
          if (error?.response?.status === 401) {
            console.log('Token invalid or expired, clearing auth state');
            clearAuth();
          }
        }
      }
    };

    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route element={<AuthLayout />}>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
      </Route>

      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        {/* ── Command Center ───────────────────────────────────────────────── */}
        <Route path="/dashboard" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <DashboardPage />
          </PermissionRoute>
        } />
        <Route path="/analysis" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view', 'ai:query']}>
            <AnalysisPage />
          </PermissionRoute>
        } />

        {/* ── Missions ─────────────────────────────────────────────────────── */}
        <Route path="/missions" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <MissionsListPage />
          </PermissionRoute>
        } />
        <Route path="/missions/:id" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <MissionDetailPage />
          </PermissionRoute>
        } />
        <Route path="/missions/upload" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write', 'telemetry:upload']}>
            <LargeFileUploadPage />
          </PermissionRoute>
        } />
        <Route path="/missions/create" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write']}>
            <CreateMissionPage />
          </PermissionRoute>
        } />
        <Route path="/missions/:id/ingest" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write', 'telemetry:upload']}>
            <MissionIngestionPage />
          </PermissionRoute>
        } />

        {/* ── Satellite Monitoring (legacy) ────────────────────────────────── */}
        <Route path="/satellite/boundaries" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <BoundariesMapPage />
          </PermissionRoute>
        } />
        <Route path="/satellite/imagery" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view', 'analytics:view']}>
            <ImageryPage />
          </PermissionRoute>
        } />
        <Route path="/satellite/anomalies" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <AnomalyDashboardPage />
          </PermissionRoute>
        } />

        {/* ── AgroTech Estate Intelligence ─────────────────────────────────── */}
        <Route path="/agrotech/dashboard" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <AgroTechDashboardPage />
          </PermissionRoute>
        } />
        <Route path="/agrotech/fields" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <FieldsPage />
          </PermissionRoute>
        } />
        <Route path="/agrotech/boundaries" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <FieldsPage />
          </PermissionRoute>
        } />
        <Route path="/agrotech/temporal" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <TemporalAnalysisPage />
          </PermissionRoute>
        } />
        <Route path="/agrotech/anomalies" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <AnomalyAlertsPage />
          </PermissionRoute>
        } />
        <Route path="/agrotech/survey-planner" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <SurveyMissionPlannerPage />
          </PermissionRoute>
        } />
        <Route path="/agrotech/flight-analysis" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write', 'telemetry:upload']}>
            <FlightAnalysisPage />
          </PermissionRoute>
        } />
        <Route path="/agrotech/analysis-review" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write', 'hitl:certify']}>
            <AnalysisReviewPage />
          </PermissionRoute>
        } />
        <Route path="/agrotech/remediation" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write']}>
            <RemediationMissionsPage />
          </PermissionRoute>
        } />
        <Route path="/agrotech/efficacy" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <TreatmentEfficacyPage />
          </PermissionRoute>
        } />
        <Route path="/agrotech/reports" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <CropHealthReportsPage />
          </PermissionRoute>
        } />
        <Route path="/agrotech/ask-nabhos" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <ConversationalAnalysisPage />
          </PermissionRoute>
        } />

        {/* ── Precision Planning ───────────────────────────────────────────── */}
        <Route path="/precision/variable-rate" element={
          <PermissionRoute anyPermissions={['*.*', 'precision:execute']}>
            <VariableRateScriptingPage />
          </PermissionRoute>
        } />
        <Route path="/precision/yield" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <YieldForecastingPage />
          </PermissionRoute>
        } />
        <Route path="/precision/crop-suitability" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <CropSuitabilityPage />
          </PermissionRoute>
        } />
        <Route path="/precision/land-expansion" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <LandExpansionPage />
          </PermissionRoute>
        } />
        <Route path="/precision/compliance" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view', 'analytics:view']}>
            <ChemicalCompliancePage />
          </PermissionRoute>
        } />

        {/* ── Infrastructure Intelligence ──────────────────────────────────── */}
        <Route path="/infrastructure/dashboard" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <InfraDashboardPage />
          </PermissionRoute>
        } />
        <Route path="/infrastructure/boundaries" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <AssetBoundariesPage />
          </PermissionRoute>
        } />
        <Route path="/infrastructure/temporal" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <InfraTemporalAnalysisPage />
          </PermissionRoute>
        } />
        <Route path="/infrastructure/anomalies" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <InfraAnomalyAlertsPage />
          </PermissionRoute>
        } />
        <Route path="/infrastructure/survey-planner" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <InfraSurveyMissionPlannerPage />
          </PermissionRoute>
        } />
        <Route path="/infrastructure/flight-analysis" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write', 'telemetry:upload']}>
            <InfraFlightAnalysisPage />
          </PermissionRoute>
        } />
        <Route path="/infrastructure/analysis-review" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write', 'hitl:certify']}>
            <InfraAnalysisReviewPage />
          </PermissionRoute>
        } />
        <Route path="/infrastructure/response" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write']}>
            <InfraResponsePlanningPage />
          </PermissionRoute>
        } />
        <Route path="/infrastructure/construction" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <ConstructionProgressPage />
          </PermissionRoute>
        } />
        <Route path="/infrastructure/encroachment" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <EncroachmentDetectionPage />
          </PermissionRoute>
        } />

        {/* ── Oil & Gas Intelligence ───────────────────────────────────────── */}
        <Route path="/oil-gas/dashboard" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <OilGasDashboardPage />
          </PermissionRoute>
        } />
        <Route path="/oil-gas/boundaries" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <CorridorBoundariesPage />
          </PermissionRoute>
        } />
        <Route path="/oil-gas/temporal" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <OilGasTemporalAnalysisPage />
          </PermissionRoute>
        } />
        <Route path="/oil-gas/anomalies" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <PipelineAnomalyAlertsPage />
          </PermissionRoute>
        } />
        <Route path="/oil-gas/survey-planner" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <OgiSurveyPlannerPage />
          </PermissionRoute>
        } />
        <Route path="/oil-gas/flight-analysis" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write', 'telemetry:upload']}>
            <OilGasFlightAnalysisPage />
          </PermissionRoute>
        } />
        <Route path="/oil-gas/analysis-review" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write', 'hitl:certify']}>
            <OilGasAnalysisReviewPage />
          </PermissionRoute>
        } />
        <Route path="/oil-gas/response" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write']}>
            <ResponsePlanningPage />
          </PermissionRoute>
        } />
        <Route path="/oil-gas/closure" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write']}>
            <IncidentClosurePage />
          </PermissionRoute>
        } />
        <Route path="/oil-gas/exploration" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <UpstreamExplorationPage />
          </PermissionRoute>
        } />
        <Route path="/oil-gas/plume-analyzer" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <PlumeLeakAnalyzerPage />
          </PermissionRoute>
        } />
        <Route path="/oil-gas/facility-safety" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <FacilitySafetyPage />
          </PermissionRoute>
        } />
        <Route path="/oil-gas/erp-integration" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write']}>
            <ErpIntegrationPage />
          </PermissionRoute>
        } />
        <Route path="/oil-gas/value-realization" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <ValueRealizationPage />
          </PermissionRoute>
        } />
        <Route path="/oil-gas/regulatory" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <RegulatoryReportingPage />
          </PermissionRoute>
        } />

        {/* ── Environmental Geotech ────────────────────────────────────────── */}
        <Route path="/environmental/dashboard" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <EnvDashboardPage />
          </PermissionRoute>
        } />
        <Route path="/environmental/boundaries" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <ZoneBoundariesPage />
          </PermissionRoute>
        } />
        <Route path="/environmental/temporal" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <EnvTemporalAnalysisPage />
          </PermissionRoute>
        } />
        <Route path="/environmental/anomalies" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <EnvAnomalyAlertsPage />
          </PermissionRoute>
        } />
        <Route path="/environmental/survey-planner" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <EnvSurveyMissionPlannerPage />
          </PermissionRoute>
        } />
        <Route path="/environmental/flight-analysis" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write', 'telemetry:upload']}>
            <EnvFlightAnalysisPage />
          </PermissionRoute>
        } />
        <Route path="/environmental/analysis-review" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write', 'hitl:certify']}>
            <EnvAnalysisReviewPage />
          </PermissionRoute>
        } />
        <Route path="/environmental/enforcement" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write']}>
            <EnforcementActionsPage />
          </PermissionRoute>
        } />
        <Route path="/environmental/carbon" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <CarbonCreditVerificationPage />
          </PermissionRoute>
        } />
        <Route path="/environmental/geohazard" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <GeohazardRiskPage />
          </PermissionRoute>
        } />

        {/* ── Linear Assets Intelligence (P3) ──────────────────────────────── */}
        <Route path="/linear-assets/dashboard" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <LinearAssetsDashboardPage />
          </PermissionRoute>
        } />
        <Route path="/linear-assets/corridors" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <LinearCorridorBoundariesPage />
          </PermissionRoute>
        } />
        <Route path="/linear-assets/temporal" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <LinearTemporalAnalysisPage />
          </PermissionRoute>
        } />
        <Route path="/linear-assets/encroachment" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <LinearEncroachmentPage />
          </PermissionRoute>
        } />
        <Route path="/linear-assets/obstructions" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <LinearTrackObstructionsPage />
          </PermissionRoute>
        } />
        <Route path="/linear-assets/survey-planner" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <LinearSurveyPlannerPage />
          </PermissionRoute>
        } />
        <Route path="/linear-assets/flight-analysis" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write', 'telemetry:upload']}>
            <LinearFlightAnalysisPage />
          </PermissionRoute>
        } />
        <Route path="/linear-assets/analysis-review" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write', 'hitl:certify']}>
            <LinearAnalysisReviewPage />
          </PermissionRoute>
        } />
        <Route path="/linear-assets/response" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:write']}>
            <LinearResponsePlanningPage />
          </PermissionRoute>
        } />
        <Route path="/linear-assets/work-orders" element={
          <PermissionRoute anyPermissions={['*.*', 'missions:view']}>
            <LinearWorkOrdersPage />
          </PermissionRoute>
        } />
        <Route path="/linear-assets/reports" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <LinearReportsPage />
          </PermissionRoute>
        } />
        <Route path="/linear-assets/efficacy" element={
          <PermissionRoute anyPermissions={['*.*', 'analytics:view']}>
            <LinearEfficacyPage />
          </PermissionRoute>
        } />

        {/* ── Utility (open to all authenticated users) ───────────────────── */}
        <Route path="/help" element={<HelpPage />} />
        <Route path="/profile" element={<ProfilePage />} />

        {/* ── Admin ────────────────────────────────────────────────────────── */}
        <Route path="/admin" element={
          <PermissionRoute anyPermissions={['*.*', 'organization:admin']}>
            <AdminSettingsPage />
          </PermissionRoute>
        } />

        {/* ── Enterprise Routes ────────────────────────────────────────────── */}
        <Route path="/enterprise/integrity" element={
          <PermissionRoute anyPermissions={['*.*', 'integrity:view']}>
            <IntegrityDashboard />
          </PermissionRoute>
        } />
        <Route path="/enterprise/audit" element={
          <PermissionRoute anyPermissions={['*.*', 'audit:view']}>
            <AuditTrailViewer />
          </PermissionRoute>
        } />
        <Route path="/enterprise/mfa" element={
          <PermissionRoute anyPermissions={['*.*', 'organization:admin', 'security:manage']}>
            <MFAManagement />
          </PermissionRoute>
        } />
        <Route path="/enterprise/notifications" element={
          <PermissionRoute anyPermissions={['*.*', 'notifications:manage']}>
            <NotificationsCenter />
          </PermissionRoute>
        } />
        <Route path="/enterprise/webhooks" element={
          <PermissionRoute anyPermissions={['*.*', 'webhooks:manage']}>
            <WebhooksConfiguration />
          </PermissionRoute>
        } />
        <Route path="/enterprise/hierarchy" element={
          <PermissionRoute anyPermissions={['*.*', 'hierarchy:manage']}>
            <HierarchyManager />
          </PermissionRoute>
        } />
        <Route
          path="/platform-admin/verticals"
          element={
            <PermissionRoute permission="*.*">
              <ModuleMarketplace />
            </PermissionRoute>
          }
        />
        <Route
          path="/platform-admin/dashboard"
          element={
            <PermissionRoute permission="*.*">
              <PlatformAdminDashboardPage />
            </PermissionRoute>
          }
        />

        {/* V0.2 Platform Admin Routes - Require wildcard permission */}
        <Route
          path="/platform-admin/roles"
          element={
            <PermissionRoute permission="*.*">
              <RoleRegistryPage />
            </PermissionRoute>
          }
        />
        <Route
          path="/platform-admin/organizations"
          element={
            <PermissionRoute permission="*.*">
              <OrganizationProvisioningPage />
            </PermissionRoute>
          }
        />
        <Route
          path="/platform-admin/analytics"
          element={
            <PermissionRoute permission="*.*">
              <PlatformAnalyticsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="/platform-admin/membership-requests"
          element={
            <PermissionRoute permission="*.*">
              <MembershipRequestsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="/platform-admin/users"
          element={
            <PermissionRoute permission="*.*">
              <PlatformUserManagementPage />
            </PermissionRoute>
          }
        />
        <Route
          path="/platform-admin/user-audit"
          element={
            <PermissionRoute permission="*.*">
              <UserManagementAuditPage />
            </PermissionRoute>
          }
        />
        <Route
          path="/platform-admin/demo-setup"
          element={
            <PermissionRoute permission="*.*">
              <DemoSetupPage />
            </PermissionRoute>
          }
        />
        
        {/* V0.2 Organization Admin Routes - Require organization management permissions */}
        <Route
          path="/org-admin/users"
          element={
            <PermissionRoute anyPermissions={['*.*', 'organization:admin', 'users:write']}>
              <UserManagementPage />
            </PermissionRoute>
          }
        />
        <Route
          path="/settings/role-screen-access"
          element={
            <PermissionRoute anyPermissions={['*.*', 'organization:admin', 'roles:read']}>
              <RoleScreenAccessPage />
            </PermissionRoute>
          }
        />

        {/* Settings Routes */}
        <Route
          path="/settings/ai-configuration"
          element={
            <PermissionRoute anyPermissions={['*.*', 'organization:admin', 'settings:write']}>
              <AIConfigurationPage />
            </PermissionRoute>
          }
        />
        <Route
          path="/settings/approved-assets"
          element={
            <PermissionRoute anyPermissions={['*.*', 'organization:admin', 'assets:manage']}>
              <ApprovedAssetsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="/settings/units-of-measure"
          element={
            <PermissionRoute anyPermissions={['*.*', 'organization:admin', 'settings:write']}>
              <UnitsOfMeasureSettingsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="/settings/flight-data-pipeline"
          element={
            <PermissionRoute anyPermissions={['*.*', 'organization:admin', 'settings:write']}>
              <FlightDataPipelineSettingsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="/settings/satellite-monitoring"
          element={
            <PermissionRoute anyPermissions={['*.*', 'organization:admin', 'settings:write']}>
              <SatelliteMonitoringSettingsPage />
            </PermissionRoute>
          }
        />
      </Route>

      {/* Default Route */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* 404 Route */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default App;

// Made with Bob
